import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Ticket, QrCode, Settings, Mail } from "lucide-react";
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import EventCard from "@/components/common/EventCard";
import ReservationForm from "@/components/forms/ReservationForm";
import MusicPlayer from "@/components/common/MusicPlayer";

const HomePage = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (e) {
      console.error("Error fetching events:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
  };

  const handleBackToEvents = () => {
    setSelectedEvent(null);
    fetchEvents();
  };

  if (selectedEvent) {
    return <ReservationForm event={selectedEvent} onBack={handleBackToEvents} />;
  }

  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <MusicPlayer hasActiveEvents={events.length > 0} />
      <div className="main-container-wide relative z-10">
        {/* Header */}
        <div className="text-center mb-8 fade-in-up">
          <div className="relative inline-block" data-testid="logo-title">
            <h1 className="font-logo text-4xl md:text-5xl cursor-default text-white neon-glow glitch-effect">
              TAYTANTOS
            </h1>
            <span 
              className="absolute text-white neon-glow text-2xl md:text-3xl"
              style={{fontFamily: "'Monoton', cursive", right: '-15px', top: '100%', marginTop: '0px'}}
            >
              pass
            </span>
          </div>
        </div>

        {/* Events Grid */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl text-white mb-6 uppercase tracking-wide text-center">
            Próximos Eventos
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="spinner-large"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <Ticket size={48} className="text-[var(--text-muted)] mx-auto mb-4" />
              <p className="text-[var(--text-muted)]">No hay eventos disponibles</p>
            </div>
          ) : (
            <div className="events-grid" data-testid="events-grid">
              {events.map((event) => (
                <EventCard 
                  key={event.id} 
                  event={event} 
                  onSelect={handleSelectEvent}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="text-center mt-8 pt-8 border-t border-[var(--border-color)]">
          {/* Admin & Scanner Links */}
          <div className="flex justify-center gap-6 mb-6">
            <button 
              onClick={() => navigate('/scanner')}
              className="text-[var(--text-muted)] hover:text-[var(--primary)] text-xs uppercase tracking-widest transition-colors"
              data-testid="scanner-link-home"
            >
              <QrCode size={14} className="inline mr-2" />
              Escanear Entrada
            </button>
            <button 
              onClick={() => navigate('/admin')}
              className="text-[var(--text-muted)] hover:text-[var(--primary)] text-xs uppercase tracking-widest transition-colors"
              data-testid="admin-link"
            >
              <Settings size={14} className="inline mr-2" />
              Administración
            </button>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-4">© 2025 TAYTANTOS - Todos los derechos reservados</p>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            <Mail size={12} className="inline mr-1" />
            <a href="mailto:entradas@taytantos.eu" className="hover:text-[var(--primary)] transition-colors">
              entradas@taytantos.eu
            </a>
          </p>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            <a href="/privacidad" className="hover:text-[var(--primary)] transition-colors underline">
              Política de Protección de Datos y Condiciones Legales
            </a>
          </p>
          {/* LW Laser Logo */}
          <div className="flex justify-center mt-2">
            <img 
              src="/lw-laser-logo.png" 
              alt="LW Laser - The Power of Light" 
              className="h-20 object-contain"
            />
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;
